import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError, ErrorCode, RequestState } from 'shared/api';

import { 
    fetchCandidateProfile, 
    updateCandidateProfile 
} from '../api';
import { 
    CandidateProfileFormData,
    CandidateViewModel } from '../types';
import { formDataToUpdateProfile } from '../utils';

interface CandidateProfileState {
    profile: CandidateViewModel | null;
    loadState: RequestState;
    updateState: RequestState;
}

const initialState: CandidateProfileState = {
    profile: null,
    loadState: {
        status: 'idle'
    },
    updateState: {
        status: 'idle'
    }
};

const candidateProfileSlice = createSlice({
    name: 'candidateProfile',
    initialState,
    reducers: {
        resetState: () => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCandidateProfileThunk.pending, (state) => {
            state.loadState = { status: 'pending' };
        });

        builder.addCase(fetchCandidateProfileThunk.fulfilled, (state, action) => {
            state.loadState = { status: 'success' };
            state.profile = action.payload;
        });

        builder.addCase(fetchCandidateProfileThunk.rejected, (state, { payload }) => {
            state.loadState = { status: 'error', errorCode: payload };
        });

        builder.addCase(updateCandidateProfileThunk.pending, (state) => {
            state.updateState = { status: 'pending' };
        });

        builder.addCase(updateCandidateProfileThunk.fulfilled, (state, action) => {
            state.updateState = { status: 'success' };
            state.profile = action.payload;
        });

        builder.addCase(updateCandidateProfileThunk.rejected, (state, { payload }) => {
            state.updateState = { status: 'error', errorCode: payload };
        });
    }
});

export const {
    resetState
} = candidateProfileSlice.actions;

export default candidateProfileSlice.reducer;

export const fetchCandidateProfileThunk = createAsyncThunk<
    CandidateViewModel,
    void,
    { rejectValue: ErrorCode }
>(
    `${ candidateProfileSlice.name }/fetch`,
    async (_, thunkAPI) => {
        const response = await fetchCandidateProfile();

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);

export const updateCandidateProfileThunk = createAsyncThunk<
    CandidateViewModel,
    { formData: CandidateProfileFormData; isRoleSpecialtyEnabled: boolean },
    { rejectValue: ErrorCode }
>(
    `${ candidateProfileSlice.name }/update`,
    async ({ formData, isRoleSpecialtyEnabled }, thunkAPI) => {
        const updateRequest = formDataToUpdateProfile(formData, isRoleSpecialtyEnabled);

        const response = await updateCandidateProfile(updateRequest);

        if (response instanceof ApiError) {
            return thunkAPI.rejectWithValue(response.errorCode);
        }

        return response;
    }
);
